import { SuccessfullAddRegionDialog } from '../components/regions/successfullAddRegionDialog';
import { AddRegionDialog } from '../components/regions/addRegionDialog';
import RegionFiltration from '../components/regions/regionFiltration';
import RegionPageHeader from '../components/regions/regionPageHeader';
import { useState, useCallback, useEffect } from 'react';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { Divider, Box, LinearProgress } from '@mui/material';
import { useHasPermission } from '../hooks/useHasPermission';
import RegionList from '../components/regions/regionList';
import RegionGrid from '../components/regions/regionGrid';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import {
    ListNavigationProvider,
    useListPageNavigation,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import '../css/roles/roles.css';
import axios from 'axios';

const Regions = () => {

    const hasRegionCreatePermission = useHasPermission('region_create');

    const [openAddRegion, setOpenAddRegion] = useState(false);
    const [openSuccessfulAddRegion, setOpenSuccessfulAddRegion] = useState(false);

    const [loading, setLoading] = useState(true);
    const [regions, setRegions] = useState([])
    const [totalRegions, setTotalRegions] = useState('');
    const [filteredRegionCount, setFilteredRegionCount] = useState('');
    const [regionFetched, setRegionFetched] = useState(0);
    const [selectedDisplayMode, setSelectedDisplayMode] = useState(JSON.parse(localStorage.getItem('selectedRegionsDisplayMode')) || 'grid');
    const [regionFilters, setRegionFilters] = useState(
        JSON.parse(localStorage.getItem('regionFilters')) ||
        { debouncedSearch: '' }
    );
    const defaultSortBy = {
        name: null,
        description: null,
    };
    const [sortBy, setSortBy] = useState(() => {
        const storedSortBy = JSON.parse(localStorage.getItem('regionSortBy')) || {};
        return { ...defaultSortBy, ...storedSortBy }; // Merge defaults with stored sortBy
    });

    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'regions',
        pathname: '/dashboard/regions',
        displayMode: selectedDisplayMode,
        itemSizes: { grid: 280, list: 70 },
        resetOnFilters: regionFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });
    const limit = 30;

    const filtersEmpty = isObjectEmpty(regionFetched);

    useEffect(() => {
        setLoading(true)
    }, [regionFilters]);

    useEffect(() => {
        localStorage.setItem('regionFilters', JSON.stringify(regionFilters));
    }, [regionFilters]);

    useEffect(() => {
        localStorage.setItem('regionSortBy', JSON.stringify(sortBy));
    }, [sortBy]);

    useEffect(() => {
        localStorage.setItem('selectedRegionsDisplayMode', JSON.stringify(selectedDisplayMode));
    }, [selectedDisplayMode]);

    const fetchRegions = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('region/list', { params: { offset: adjustedOffset, limit, filters: regionFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedRegion = [];
                // Update the fetched records in their correct positions
                response.data.regions.forEach((region, index) => { updatedRegion[adjustedOffset + index] = region });
                // Update the state with the new regions array
                setRegions(updatedRegion);
                setFilteredRegionCount(response?.data?.filteredRegionsCount);
                setTotalRegions(response?.data?.totalRegions);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching regions:', error);
            CustomErrorLogger(error, 'fetch regions in regions page');
        }
    }, [offset, regionFilters, sortBy]);

    useEffect(() => {
        fetchRegions()
    }, [fetchRegions]);


    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Regions" list={true}>
            <RegionPageHeader setOpenAddRegion={setOpenAddRegion} />
            <Divider />

            {loading ?
                <Box>
                    <RegionFiltration
                        filtersEmpty={filtersEmpty}
                        regionFilters={regionFilters}
                        setRegionFilters={setRegionFilters}
                        totalRegions={totalRegions}
                        regionFetched={regionFetched}
                        filteredRegionCount={filteredRegionCount}
                        selectedDisplayMode={selectedDisplayMode}
                        setSelectedDisplayMode={setSelectedDisplayMode}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalRegions === 0 ?
                    <NoRecord
                        description="Aucun region ajouté pour le moment"
                        buttonTitle="Ajouter une region"
                        handleOpenDialog={() => setOpenAddRegion(true)}
                        hasPermission={hasRegionCreatePermission}
                    /> :
                    <Box>
                        <RegionFiltration
                            filtersEmpty={filtersEmpty}
                            regionFilters={regionFilters}
                            setRegionFilters={setRegionFilters}
                            totalRegions={totalRegions}
                            regionFetched={regionFetched}
                            filteredRegionCount={filteredRegionCount}
                            selectedDisplayMode={selectedDisplayMode}
                            setSelectedDisplayMode={setSelectedDisplayMode}
                        />

                        {selectedDisplayMode === 'grid' ? (
                            <RegionGrid
                                scrollRef={scrollRef}
                                regions={regions}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                setOffset={setOffset}
                                filteredRegionCount={filteredRegionCount}
                                setRegionFetched={setRegionFetched}
                            />
                        ) : (
                            <RegionList
                                scrollRef={scrollRef}
                                regions={regions}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                setOffset={setOffset}
                                filteredRegionCount={filteredRegionCount}
                                setRegionFetched={setRegionFetched}
                            />
                        )}

                    </Box>
            }

            {openAddRegion &&
                <AddRegionDialog
                    setRegions={setRegions}
                    setTotalRegions={setTotalRegions}
                    setFilteredRegionCount={setFilteredRegionCount}
                    openAddRegion={openAddRegion}
                    setOpenAddRegion={setOpenAddRegion}
                />
            }

            {openSuccessfulAddRegion &&
                <SuccessfullAddRegionDialog
                    openSuccessfulAddRegion={openSuccessfulAddRegion}
                    setOpenAddRegion={setOpenAddRegion}
                />
            }

        </Page>
        </ListNavigationProvider>
    );
};

export default Regions;